import { Button } from '@creditornot/cb-button';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Image } from 'apis/asset-repository-service/types';

import { ImageGalleryBrowseImages } from '../components/ImageGalleryBrowseImages';
import { IMAGE_BASE_URL } from '../constants';

const Root = styled.div`
  position: absolute;
  background-color: ${color.bg};
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
`;

type Props = {
  isVisible: boolean;
  onClose: () => void;
  onChange: (selectedImages: { id: string; imageUrl: string }[]) => void;
  limit: number;
  assignedImagesIds: string[];
};

export type ImageGalleryBrowseOverlayHandler = {
  removeSelectionById: (removedImageId: string) => void;
};

export const ImageGalleryBrowseOverlay = forwardRef<ImageGalleryBrowseOverlayHandler, Props>(
  ({ isVisible, onClose, limit, onChange, assignedImagesIds }, ref) => {
    const [selectedImages, setSelectedImages] = useState<string[]>([]);

    const toggleSelectedImage = (toggledImage: string) => {
      setSelectedImages(prev =>
        prev.indexOf(toggledImage) !== -1
          ? prev.filter(image => image !== toggledImage)
          : limit > prev.length
            ? [...prev, toggledImage]
            : prev,
      );
    };

    const removeSelectionById = (removedImageId: string) => {
      setSelectedImages(prev => {
        onChange(prev.filter(id => id !== removedImageId).map(id => ({ id, imageUrl: IMAGE_BASE_URL + id })));
        return prev.filter(id => id !== removedImageId);
      });
    };

    const handleImageCardClick = (asset: Image) => {
      toggleSelectedImage(asset.id);
    };

    const handleAddToItemButtonClick = () => {
      if (!selectedImages) {
        return;
      }
      onChange(selectedImages.map(id => ({ id, imageUrl: IMAGE_BASE_URL + id })));
      onClose();
    };

    useImperativeHandle(ref, () => ({
      removeSelectionById,
    }));

    useEffect(() => {
      setSelectedImages(assignedImagesIds);
    }, [assignedImagesIds]);

    if (!isVisible) {
      return null;
    }

    return (
      <Root>
        <ImageGalleryBrowseImages
          onHeaderExitClick={onClose}
          headerContent={
            <Button
              variant="primary"
              size="tiny"
              disabled={selectedImages.length < 1}
              onClick={handleAddToItemButtonClick}
            >
              <FormattedMessage id="shared-menu.editor.image-gallery.edit-single-offer.attach-to-item" />
            </Button>
          }
          onImageCardClick={handleImageCardClick}
          selectedImagesIds={selectedImages}
        />
      </Root>
    );
  },
);
